import { createPopper } from '@popperjs/core'
import DOMPurify from 'dompurify'
import Vue from 'vue'

import { highlightKeywords } from '@/modules/keywords/logic/keywordHighlighting'

let localRouter = null
let localStore = null

function handleLinkClick(e) {
  const element = e.target.closest('a')
  if (!element) {
    return true
  }
  // Ignore external links, # Links, Pdf Links
  const target = element.getAttribute('target')
  const href = element.getAttribute('href')
  if (target === '_blank' || href.startsWith('#') || href.endsWith('.pdf') || e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) {
    return true
  }

  // Ignore non same-host links
  const targetUrl = new URL(href)

  if (!localStore.getters['auth/config'].app_domains.includes(targetUrl.host)) {
    return true
  }

  e.stopPropagation()
  e.preventDefault()

  localRouter.push(targetUrl.pathname)

  return false
}

export default function (store, router) {
  localRouter = router
  localStore = store
  Vue.directive('internallinks', {
    // When the bound element is inserted into the DOM...
    inserted(el) {
      el.addEventListener('click', handleLinkClick)
    },
  })
  Vue.directive('highlightKeywords', {
    // When the bound element is inserted into the DOM...
    async inserted(el) {
      if (!store.getters['auth/config'].settings.module_keywords || !store.getters['auth/isLoggedIn']) {
        return
      }
      await highlightKeywords(el, store)
    },
  })
  Vue.directive('click-outside', {
    bind(element, binding, vnode) {
      element.clickOutsideEvent = function (event) {
        if (element !== event.target && !element.contains(event.target)) {
          vnode.context[binding.expression](event)
        }
      }
      document.body.addEventListener('click', element.clickOutsideEvent)
    },
    unbind(element) {
      document.body.removeEventListener('click', element.clickOutsideEvent)
    },
  })

  // Sanitizes and renders HTML safely
  Vue.directive('safe-chat-html', {
    bind(el, binding) {
      el.sanitize = (content) => {
        const sanitized = DOMPurify.sanitize(content, {
          ALLOWED_TAGS: ['span', 'br'],
          ALLOWED_ATTR: ['class'],
        })
        // Ensure that *only* spans with class="c-chat__keyword" remain
        const temp = document.createElement('div')
        temp.innerHTML = sanitized
        temp.querySelectorAll('span').forEach((node) => {
          if (!node.classList.contains('c-chat__keyword')) {
            node.replaceWith(document.createTextNode(node.textContent))
          } else {
            // enforce exactly the required class
            node.setAttribute('class', 'c-chat__keyword')
          }
        })
        return temp.innerHTML
      }
      el.innerHTML = el.sanitize(binding.value || '')
    },
    update(el, binding) {
      if (binding.value !== binding.oldValue) {
        el.innerHTML = el.sanitize(binding.value || '')
      }
    },
  })

  // Popper Directive to show popover
  // ex: v-popper="someText" or v-popper="{ content: 'text', html: true, activatorClass: 'c-popper__activator' }"
  // to make the popover open at a specific element, add class="c-popper__activator" to the element
  Vue.directive('popper', {
    bind(el, binding) {
      const val = binding.value
      const isObj = val && typeof val === 'object' && !Array.isArray(val)

      // Validation: content is required
      if (isObj && !val.content) {
        console.error('v-popper: content is required when using object format')
        return
      }

      const state = {
        instance: null,
        popperEl: null,
        arrowEl: null,
        content: isObj ? val.content : val,
        html: isObj ? !!val.html : !!binding.modifiers.html,
        activatorClass: val.activatorClass ?? '.c-popper__activator',
        placement: isObj ? (val.placement ?? 'top') : 'top',
      }
      if (!state.activatorClass.startsWith('.')) {
        state.activatorClass = `.${state.activatorClass}`
      }

      const setContent = () => {
        if (!state.popperEl) return
        // reset content but preserve arrow later
        const arrow = state.arrowEl
        state.popperEl.innerHTML = ''

        // Add content
        if (state.content) {
          const contentEl = document.createElement('div')
          contentEl.classList.add('c-popper__content')
          if (state.html) {
            const sanitized = DOMPurify.sanitize(state.content, {
              ALLOWED_TAGS: ['b', 'i', 'u', 'a', 'strong', 'em', 'span', 'br'],
              ALLOWED_ATTR: ['class'],
            })
            contentEl.innerHTML = sanitized
          } else {
            contentEl.textContent = state.content
          }
          state.popperEl.appendChild(contentEl)
        }

        if (arrow) {
          state.popperEl.appendChild(arrow)
        }
      }

      const createArrow = () => {
        const arrow = document.createElement('div')
        arrow.setAttribute('data-popper-arrow', '')
        arrow.classList.add('c-popper__arrow')
        return arrow
      }

      const show = () => {
        if (state.instance) return
        const arrow = createArrow()
        const pop = document.createElement('div')
        pop.classList.add('c-popper', 't-body3')

        state.popperEl = pop
        state.arrowEl = arrow
        setContent()
        document.body.appendChild(pop)

        // To specify the element that the popper should be attached to
        const referenceEl = el.querySelector(state.activatorClass) || el
        state.instance = createPopper(referenceEl, pop, {
          placement: state.placement,
          modifiers: [
            {
              name: 'offset',
              options: { offset: [0, 10] },
            },
            {
              name: 'preventOverflow',
              options: { boundary: 'viewport' },
            },
            {
              name: 'arrow',
              options: { element: arrow, padding: 5 },
            },
          ],
        })
      }

      const hide = () => {
        if (state.instance) {
          state.instance.destroy()
          state.instance = null
        }
        if (state.popperEl && state.popperEl.parentNode) {
          state.popperEl.parentNode.removeChild(state.popperEl)
        }
        state.popperEl = null
        state.arrowEl = null
      }

      const onMouseEnter = () => show()
      const onMouseLeave = () => hide()
      const onFocusIn = () => show()
      const onFocusOut = () => hide()

      // we register handlers to be able to remove them later when "unbind" is called
      el.__popper = {
        state,
        handlers: {
          onMouseEnter, onMouseLeave, onFocusIn, onFocusOut,
        },
      }

      el.addEventListener('mouseenter', onMouseEnter)
      el.addEventListener('mouseleave', onMouseLeave)
      el.addEventListener('focusin', onFocusIn)
      el.addEventListener('focusout', onFocusOut)

      // keyboard accessibility
      if (!el.hasAttribute('tabindex')) {
        el.setAttribute('tabindex', '0')
      }
    },
    unbind(el) {
      const data = el.__popper
      if (!data) return
      el.removeEventListener('mouseenter', data.handlers.onMouseEnter)
      el.removeEventListener('mouseleave', data.handlers.onMouseLeave)
      el.removeEventListener('focusin', data.handlers.onFocusIn)
      el.removeEventListener('focusout', data.handlers.onFocusOut)
      if (data.state.instance) {
        data.state.instance.destroy()
      }
      if (data.state.popperEl && data.state.popperEl.parentNode) {
        data.state.popperEl.parentNode.removeChild(data.state.popperEl)
      }
      delete el.__popper
    },
  })
}
